/**
 * Punësimet — përfundimet e suksesshme të rekrutimit.
 *
 * Nuk është kopje e dytë e Stafit dhe nuk ka tabelë të vetën. Një punësim është
 * aplikim me fazën «I punësuar»; oferta e pranuar dhe punëtori i lidhur janë të
 * dhëna që i shoqërohen kur ekzistojnë. Një tabelë «hires» do ta mbante të
 * njëjtin fakt në dy vende dhe do të lejonte që ato të mos pajtoheshin.
 *
 * Pyetja e përditshme që i përgjigjet kjo faqe: kush u punësua, nga cila pozitë
 * dhe cili aplikim, me cilën ofertë — dhe a është lidhur tashmë me Stafin, se
 * pa atë lidhje paga dhe kontrata nuk kanë ku të mbështeten.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Typography, Stack, Chip, Button, Tooltip, TextField, MenuItem,
  Alert, CircularProgress, LinearProgress,
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link, useHistory } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import recruitmentHistoryService from '../services/recruitmentHistoryService';
import applicationsService from '../services/applicationsService';
import { sourceLabel } from '../config/recruitmentSources';
import { offerStatusMeta } from '../config/recruitmentPipeline';
import StartOnboardingDialog from '../components/onboarding/StartOnboardingDialog';
import usePermissions from '../hooks/usePermissions';

const C = { ink500: '#64748B', brand: '#3E8FA3' };

const fmtDate = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  return isNaN(x) ? '—' : `${String(x.getDate()).padStart(2, '0')}.${String(x.getMonth() + 1).padStart(2, '0')}.${x.getFullYear()}`;
};

export default function HiresPage() {
  const history = useHistory();
  const { can } = usePermissions();
  const canStartOnboarding = can('onboarding.create') || can('onboarding.manage');
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState({ search: '', staff: 'all', recruiter: 'all', from: '', to: '' });
  const [recruiters, setRecruiters] = useState([]);
  // Dorëzimi te onboarding-u nis KËTU, sepse këtu HR-ja e sheh punësimin.
  // Vetë procesi pastaj i përket Stafit; rekrutimi nuk e ndjek më.
  const [startFor, setStartFor] = useState(null);
  const [started, setStarted] = useState('');

  useEffect(() => { applicationsService().recruiters().then(setRecruiters); }, []);

  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(filters.search.trim()), 300);
    return () => clearTimeout(t);
  }, [filters.search]);
  useEffect(() => { setPage(0); }, [debounced, filters.staff, filters.recruiter, filters.from, filters.to]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const d = await recruitmentHistoryService().hires({
      page: page + 1, limit: rowsPerPage, search: debounced,
      staff: filters.staff, recruiter: filters.recruiter, from: filters.from, to: filters.to,
    });
    if (d.error) setError(d.error);
    setRows(d.data || []);
    setPagination(d.pagination || pagination);
    setStats(d.stats || null);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debounced, filters.staff, filters.recruiter, filters.from, filters.to]);

  useEffect(() => { load(); }, [load]);

  const setF = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const unlinked = useMemo(() => (stats ? stats.total - stats.linked : 0), [stats]);

  return (
    <Box>
      <PageHeader title="Punësimet" subtitle="Përfundimet e suksesshme të rekrutimit — nga pozita te punonjësi" />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <Card sx={{ flex: 1, borderLeft: `4px solid ${C.brand}` }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Punësime gjithsej</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{stats?.total ?? 0}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Këtë muaj</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{stats?.this_month ?? 0}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Pa lidhje me Stafin</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: unlinked ? '#B45309' : undefined }}>{unlinked}</Typography>
            <Typography variant="caption" color="text.secondary">paga dhe kontrata varen nga kjo lidhje</Typography>
          </CardContent>
        </Card>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField size="small" label="Kërko" placeholder="Emër ose email" value={filters.search}
              onChange={setF('search')} sx={{ flex: 1, minWidth: 180 }} />
            <TextField select size="small" label="Rekrutuesi" value={filters.recruiter} onChange={setF('recruiter')} sx={{ minWidth: 170 }}>
              <MenuItem value="all">Të gjithë</MenuItem>
              <MenuItem value="unassigned">Pa rekrutues</MenuItem>
              {recruiters.map((u) => <MenuItem key={u.id} value={String(u.id)}>{u.name}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Stafi" value={filters.staff} onChange={setF('staff')} sx={{ minWidth: 160 }}>
              <MenuItem value="all">Të gjithë</MenuItem>
              <MenuItem value="linked">Të lidhur</MenuItem>
              <MenuItem value="unlinked">Pa lidhje</MenuItem>
            </TextField>
            <TextField size="small" type="date" label="Prej" InputLabelProps={{ shrink: true }} value={filters.from} onChange={setF('from')} />
            <TextField size="small" type="date" label="Deri" InputLabelProps={{ shrink: true }} value={filters.to} onChange={setF('to')} />
            <Button size="small" variant="outlined"
              onClick={() => setFilters({ search: '', staff: 'all', recruiter: 'all', from: '', to: '' })}>Reseto</Button>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {started && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setStarted('')}
          action={<Button size="small" onClick={() => history.push(started)}>Hape</Button>}>
          Procesi i onboarding-ut u krijua.
        </Alert>
      )}

      <Card>
        <CardContent>
          {loading ? (
            <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={22} /></Stack>
          ) : rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Asnjë punësim nuk i përgjigjet filtrave. Një kandidat shfaqet këtu sapo aplikimi i tij kalon te faza «I punësuar».
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Punonjësi</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Pozita</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Departamenti</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Punësuar</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Fillimi</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Rekrutuesi</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Burimi</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Oferta</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Stafi</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Veprime</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => {
                    const st = r.offerStatus ? offerStatusMeta(r.offerStatus) : null;
                    const onb = r.workerId && r.onboardingTotal > 0;
                    return (
                      <TableRow key={r.applicationId} hover>
                        <TableCell>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{r.name}</Typography>
                          <Typography sx={{ fontSize: 11.5, color: C.ink500 }}>{r.email || '—'}</Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{r.position || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{r.departmentName || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmtDate(r.hiredAt)}</TableCell>
                        <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmtDate(r.startDate)}</TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{r.recruiterName || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{sourceLabel(r.source)}</TableCell>
                        <TableCell>
                          {st
                            ? <Chip size="small" variant="outlined" color={st.color} label={st.label} sx={{ height: 20, fontSize: '.68rem' }} />
                            : <Typography sx={{ fontSize: 12, color: C.ink500 }}>pa ofertë</Typography>}
                        </TableCell>
                        <TableCell>
                          {r.workerId ? (
                            <Stack spacing={0.25} sx={{ minWidth: 110 }}>
                              <Link to={`/workerlist/editworker/${r.workerId}`} style={{ fontSize: 12.5, color: C.brand, fontWeight: 600 }}>
                                {r.workerName || 'Punonjës'}
                              </Link>
                              {onb && (
                                <>
                                  <Typography sx={{ fontSize: 10.5, color: C.ink500 }}>
                                    onboarding {r.onboardingDone}/{r.onboardingTotal}
                                  </Typography>
                                  <LinearProgress variant="determinate"
                                    value={(r.onboardingDone / r.onboardingTotal) * 100}
                                    sx={{ height: 4, borderRadius: 2 }} />
                                </>
                              )}
                            </Stack>
                          ) : (
                            // Asnjë punonjës nuk krijohet vetvetiu nga një ofertë e
                            // pranuar: paga dhe kontrata varen nga këto të dhëna, dhe
                            // një dublikatë këtu kushton më shumë se një hap manual.
                            <Tooltip title="Hap aplikimin dhe zgjidh punonjësin te fusha «Punëtori»">
                              <Chip size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '.66rem' }}
                                label="Ende pa Staf" />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {/* Kur procesi ekziston, butoni çon te ai — jo te një
                                ftesë për ta krijuar sërish. */}
                            {r.onboardingId ? (
                              <Button size="small" sx={{ textTransform: 'none', fontSize: '.72rem', minWidth: 0 }}
                                onClick={() => history.push(`/onboarding/${r.onboardingId}`)}>
                                Onboarding
                              </Button>
                            ) : canStartOnboarding && (
                              <Tooltip title="Krijo procesin e onboarding-ut për këtë punësim">
                                <Button size="small" variant="outlined"
                                  sx={{ textTransform: 'none', fontSize: '.72rem', minWidth: 0 }}
                                  onClick={() => setStartFor(r)}>
                                  Fillo onboarding
                                </Button>
                              </Tooltip>
                            )}
                            <Tooltip title="Aplikimi">
                              <Button size="small" sx={{ textTransform: 'none', fontSize: '.75rem', minWidth: 0 }}
                                onClick={() => history.push(`/applications/edit/${r.applicationId}`)}>
                                Aplikimi
                              </Button>
                            </Tooltip>
                            {r.candidateId && (
                              <Tooltip title="Historiku i këtij personi">
                                <Button size="small" sx={{ textTransform: 'none', fontSize: '.75rem', minWidth: 0 }}
                                  startIcon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
                                  onClick={() => history.push(`/recruitment-history?candidate=${r.candidateId}`)}>
                                  Historiku
                                </Button>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {pagination.total > 0 && (
            <TablePagination
              component="div"
              count={pagination.total}
              page={Math.min(page, Math.max(0, pagination.totalPages - 1))}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage="Rreshta:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} nga ${count}`}
            />
          )}
        </CardContent>
      </Card>
      <StartOnboardingDialog
        hire={startFor}
        open={Boolean(startFor)}
        onClose={() => setStartFor(null)}
        onStarted={(r) => { setStarted(`/onboarding/${r.id}`); load(); }}
      />
    </Box>
  );
}
